import { ReactNode, useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface SnapCarouselProps {
  children: ReactNode[];
  cardWidth?: number;        // default 140 (mobile)
  cardWidthDesktop?: number; // optional desktop override at md breakpoint (768px+)
  gap?: number;              // default 10
  peekAmount?: number;       // default 20 — how much of next card is visible
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

export function SnapCarousel({
  children,
  cardWidth = 140,
  cardWidthDesktop,
  gap = 10,
  peekAmount = 20,
}: SnapCarouselProps) {
  const isDesktop = useIsDesktop();
  const resolvedWidth = isDesktop && cardWidthDesktop ? cardWidthDesktop : cardWidth;

  return (
    <div
      data-snap-container
      style={{
        display: 'flex',
        flexDirection: 'row',
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        overscrollBehaviorX: 'contain',
        WebkitOverflowScrolling: 'touch',
        gap: `${gap}px`,
        paddingInlineEnd: `${peekAmount}px`,
        // Hide scrollbar cross-browser
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      } as React.CSSProperties}
    >
      {children.map((child, index) => (
        <motion.div
          key={index}
          data-snap-item
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05, duration: 0.3 }}
          style={{
            flex: `0 0 ${resolvedWidth}px`,
            scrollSnapAlign: 'start',
          }}
        >
          {child}
        </motion.div>
      ))}
    </div>
  );
}
